import {
  InternalNetworkAssessmentEndGoalKindSchema,
  InternalNetworkAssessmentStatusSchema,
  InternalNetworkAssessmentTypeSchema,
} from '@terra/shared-schemas-orchestrator';
import { MongoObjectIdSchema } from '@terra/shared-utils';
import { z } from 'zod';

const uniqueObjectIds = ({
  schema,
  message,
}: {
  schema: z.ZodArray<typeof MongoObjectIdSchema>;
  message: string;
}) =>
  schema.superRefine((ids, ctx) => {
    const normalized = ids.map((id) => id.toLowerCase());

    if (new Set(normalized).size !== normalized.length) {
      ctx.addIssue({
        code: 'custom',
        message,
      });
    }
  });

export const InternalNetworkAssessmentSchema = z.object({
  id: z.string(),
  networkId: z.string(),
  type: InternalNetworkAssessmentTypeSchema,
  name: z.string(),
  status: InternalNetworkAssessmentStatusSchema,
  startingOwnedPrincipalIds: z.array(z.string()),
  endGoalKind: InternalNetworkAssessmentEndGoalKindSchema,
  endGoalCrownJewelIds: z.array(z.string()),
  failureReason: z.string().nullish(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const CreateInternalNetworkAssessmentSchema = z
  .object({
    name: z.string().trim().min(1),
    type: InternalNetworkAssessmentTypeSchema.default('DOMAIN_COMPROMISE'),
    startingOwnedPrincipalIds: uniqueObjectIds({
      schema: z.array(MongoObjectIdSchema).min(1),
      message: 'startingOwnedPrincipalIds must be unique',
    }),
    endGoalKind:
      InternalNetworkAssessmentEndGoalKindSchema.default('DOMAIN_COMPROMISE'),
    endGoalCrownJewelIds: uniqueObjectIds({
      schema: z.array(MongoObjectIdSchema),
      message: 'endGoalCrownJewelIds must be unique',
    }).default([]),
    runPreAssessmentDiscovery: z.boolean().default(false),
  })
  .superRefine((value, ctx) => {
    if (
      value.endGoalKind === 'CROWN_JEWELS' &&
      value.endGoalCrownJewelIds.length === 0
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'endGoalCrownJewelIds requires at least one crown jewel',
        path: ['endGoalCrownJewelIds'],
      });
    }

    if (
      value.endGoalKind === 'DOMAIN_COMPROMISE' &&
      value.endGoalCrownJewelIds.length > 0
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'endGoalCrownJewelIds must be empty for domain compromise',
        path: ['endGoalCrownJewelIds'],
      });
    }
  });

export const InternalNetworkAssessmentListResponseSchema = z.object({
  data: z.array(InternalNetworkAssessmentSchema),
  total: z.number().int().nonnegative(),
});

export const DeleteInternalNetworkAssessmentResultSchema = z.object({
  networkId: z.string(),
  assessmentId: z.string(),
});

export const AbortInternalNetworkAssessmentResultSchema = z.object({
  aborted: z.boolean(),
});

export type InternalNetworkAssessmentDto = z.infer<
  typeof InternalNetworkAssessmentSchema
>;
export type CreateInternalNetworkAssessmentDto = z.infer<
  typeof CreateInternalNetworkAssessmentSchema
>;
export type InternalNetworkAssessmentListResponseDto = z.infer<
  typeof InternalNetworkAssessmentListResponseSchema
>;
export type DeleteInternalNetworkAssessmentResultDto = z.infer<
  typeof DeleteInternalNetworkAssessmentResultSchema
>;
export type AbortInternalNetworkAssessmentResultDto = z.infer<
  typeof AbortInternalNetworkAssessmentResultSchema
>;
